import { EMAIL_REGEX } from 'constants/constants';

/**
 * Gets the width of browser window
 * @returns {number}
 */
const screenSize = () =>
  window.innerWidth ||
  document.documentElement.clientWidth ||
  document.body.clientWidth;

/**
 * Reloads the current page without search params
 * @param {object} element - DOM Element
 */
const clearSearchParams = element => {
  element.addEventListener('click', e => {
    e.preventDefault();
    window.location = window.location.href.split('?')[0];
  });
};

/**
 * Validates an email
 * @param {string} email
 * @returns true or false
 */
const isValidEmail = email => EMAIL_REGEX.test(String(email).toLowerCase());

const isMobileDevice = () => {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/.test(
      navigator.userAgent,
    )
  ) {
    return true;
  }
  return false;
};

/**
 * Capitalise First Letter of String
 * @param {string} word
 * @returns string
 */
const capitaliseFirstLetter = word =>
  typeof word === 'string'
    ? word.charAt(0).toUpperCase() + word.toLowerCase().slice(1)
    : word;

const handleUnit = unit => {
  let value;

  switch (unit) {
    case 'm':
    case 'kg':
      value = unit;
      break;
    case 'each':
    case 'Each':
      value = '';
      break;
    default:
      value = ` ${unit}`;
  }

  return value;
};

const specialCharCheck = e => {
  const specialChars = /[^a-zA-Z0-9\-\\/. +]/g;
  if (e.match(specialChars)) {
    return true;
  }
  return false;
};

const createBasketJSON = orders => {
  const lines_attributes = {};
  orders.map(
    (order, index) =>
      (lines_attributes[index] = {
        product_id: order.product_id,
        quantity: parseInt(order.quantity),
      }),
  );

  const basket = {
    basket: {
      lines_attributes,
      skip_back_order_check: 'true',
    },
  };
  return basket;
};

const displaySuccessfulUpdate = data => {
  setTimeout(() => {
    const basketInfoWrapper = document.querySelector(
      '.basket-preview-price-wrapper',
    );
    const basketItems = basketInfoWrapper.querySelector('.basket-items');
    const basketPreviewPrice = basketInfoWrapper.querySelector(
      '.basket-preview-price',
    );
    const basketCount = basketInfoWrapper.querySelector('.basket-count');
    basketInfoWrapper.classList.remove('is-loading');
    if (data) {
      basketItems.innerHTML = data.lines;
      basketPreviewPrice.innerHTML = data.total;
      basketCount.innerHTML = data.mobile_count;
    }
  }, 2500);
};

/**
 * Creates a unique array of objects by the key
 * e.g
 * const array = [ { id: 1}, {id: 1}]
 * returns: [ { id: 1}]
 * @param {Array} arr - the array to sort
 * @returns a unique array
 */
const uniqueByKey = (arr, key) => [
  ...new Map(arr.map(v => [v[key], v])).values(),
];

/**
 * @param {Array} products - list of product step ids
 * @returns formatted URL for pricing service
 */
const formatPricingParams = products => {
  const stepIds = products.map(({ step_id: id }) => id);

  return `/product_price?products=${stepIds.toString()}`;
};

const setGtmClickAttr = element =>
  element.setAttribute('data-gtm-click', 'true');

/**
 * Normalise a price:
 *  strip currency symbols and commas
 *  validate numeric input
 *  format with commas + 2 decimals
 *  optionally prepend a currency symbol
 *
 * @param {string|number} value - e.g., 2199, "2,199", "£2199.00"
 * @param {boolean} prependCurrency - whether to prepend "£"
 * @param {string} fallback - value to return when input is invalid
 * @returns {string} e.g. "£2,199.00"
 */
const normalisePrice = (value, prependCurrency = true, fallback = 'N/A') => {
  if (value === null || value === undefined || value === '') return fallback;

  let str = String(value).trim();
  str = str.replace(/£/g, '').replace(/,/g, '');

  const num = parseFloat(str);
  if (Number.isNaN(num)) return fallback;

  // format with commas and decimals
  const formatted = num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${prependCurrency ? '£' : ''}${formatted}`;
};

/**
 * check to see if a HTML element is visible on the page
 * @param {*} el - HTML node
 * @returns {boolean}
 */
const isElemVisible = el => {
  const { top: elemTop, bottom: elemBottom } = el.getBoundingClientRect();

  // Partially visible elements return true:
  const visible = elemTop < window.innerHeight && elemBottom >= 0;
  return visible;
};

/**
 * Sorts array of objects alphabetically
 * @param {string} key - key to sort by
 * @returns {function}
 */

const sortAlphabetically = key => {
  const nestedSort = (a, b) => {
    const keyA = a[key].toUpperCase();
    const keyB = b[key].toUpperCase();

    if (keyA < keyB) {
      return -1;
    }

    if (keyA > keyB) {
      return 1;
    }
    return 0;
  };

  return nestedSort;
};

/**
 * Checks if the value is an empty object, collection, map or set
 * @param {any} value
 * @returns {boolean}
 * Example usage:
 * console.log(isEmpty({})); // true
 * console.log(isEmpty([])); // true
 * console.log(isEmpty('')); // true
 * console.log(isEmpty(new Map())); // true
 * console.log(isEmpty(new Set())); // true
 * console.log(isEmpty({ a: 1 })); // false
 * console.log(isEmpty([1, 2, 3])); // false
 * console.log(isEmpty('hello')); // false
 * console.log(isEmpty(new Map([[1, 'one']]))); // false
 * console.log(isEmpty(new Set([1, 2]))); // false
 * console.log(isEmpty(0)); // false
 * console.log(isEmpty(true)); // false
 * console.log(isEmpty(() => {})); // false
 */

const isEmptyValue = value => {
  // Check for null or undefined
  if (value === null) return true;

  // Check if value is a boolean, number, function, or symbol (which are never empty)
  if (
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    typeof value === 'function' ||
    typeof value === 'symbol'
  ) {
    return false;
  }

  // Check if value is a string or an arguments object with length 0
  if (
    typeof value === 'string' ||
    (typeof value === 'object' && 'length' in value)
  ) {
    return value.length === 0;
  }

  // Check if value is a map or set with size 0
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  // Check if value is an object with no own enumerable properties
  if (typeof value === 'object' && value.constructor === Object) {
    if (Object.keys(value).length === 0) {
      return true;
    }

    return false;
  }

  // Default case
  return false;
};

const formatURLWithBrackets = (pathname, searchParams) => {
  const queryString = searchParams.toString();
  if (!queryString) return pathname;

  const cleanQuery = queryString.replace(/%5B/g, '[').replace(/%5D/g, ']');
  return `${pathname}?${cleanQuery}`;
};

export {
  screenSize,
  clearSearchParams,
  isValidEmail,
  isMobileDevice,
  capitaliseFirstLetter,
  handleUnit,
  formatPricingParams,
  setGtmClickAttr,
  specialCharCheck,
  createBasketJSON,
  displaySuccessfulUpdate,
  uniqueByKey,
  normalisePrice,
  isElemVisible,
  sortAlphabetically,
  isEmptyValue,
  formatURLWithBrackets,
};
